'use client'

import { type LocaleString } from '@/.generated/sanityTypes/graphql'
import { useLocale, useTranslations } from 'next-intl'
import { type Locale } from '@/i18n'
import { withNewLines } from '@/lib/newline'
import { cx } from '@monorepo/helper/cx'
import { useParams } from 'next/navigation'

type RevenueBlockProps = {
  title?: LocaleString
  total?: LocaleString
  lastYears?: number
}

const RevenueBlock = ({ title, total, lastYears = 6 }: RevenueBlockProps) => {
  const { year } = useParams() as { year: string }
  const locale = useLocale() as Locale
  const t = useTranslations('annualReport')

  const numOfBars = lastYears + 1
  const yearInt = parseInt(year)

  return (
    <div className="bg-red relative flex aspect-square w-full flex-col text-white md:aspect-video">
      {title && (
        <div className="flex basis-1/4 items-center justify-around text-center">
          <h2 className="mx-6 max-w-lg text-center text-xl leading-none md:mx-12 md:text-2xl">
            {withNewLines(title?.[locale])}
          </h2>
        </div>
      )}

      <div
        className={cx(
          'bg-red z-10 w-fit basis-0 pl-4 pr-2 md:pl-12',
          !title && 'mt-4 md:mt-12',
        )}
      >
        <p className="text-lg text-white md:text-xl">{t('totalRevenue')}</p>
        <p className="text-3xl text-white md:text-5xl">{total?.[locale]}</p>
      </div>

      <div className="relative -mt-3 flex-1">
        <hr className="absolute right-0 top-0 h-0 w-full border-t-4 border-dotted border-white" />
        <div className="absolute bottom-0 flex h-full w-full flex-row-reverse items-end text-center">
          {Array.from({ length: numOfBars }).map((_, i) => {
            const opacity = 1 - i / numOfBars
            const heightFactor = 1 - i / (numOfBars * 1.37)

            // Default text on columns is a transparent white
            let textColor = 'rgba(255, 255, 255, .25)'

            // First column has red text
            if (i === 0) {
              textColor = 'rgb(218, 41, 28)'
            } else if (i < 3) {
              // Next few have a transparent red
              textColor = `rgba(218, 41, 28, ${0.5 - i / numOfBars})`
            }

            return (
              <div
                key={i}
                className={cx('relative flex-1 bg-white')}
                style={{
                  background: `rgba(255, 255, 255, ${opacity})`,
                  color: textColor,
                  height: `${heightFactor * 100}%`,
                }}
              >
                <span className="absolute bottom-0 block w-full py-2">
                  {yearInt - i}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default RevenueBlock
